<template>
  <Teleport to="body">
    <Transition name="fade-slide">
      <div
        v-if="showBanner"
        class="browser-check-prompt"
        role="alert"
        aria-live="polite">
        <div class="prompt-content">
          <span class="prompt-icon">⚠️</span>
          <div class="prompt-text">
            <strong>温馨提示</strong><br />
            {{ displayMessage }}
          </div>
          <button
            class="prompt-close"
            aria-label="关闭"
            @click="closeBanner"
            >✕</button
          >
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { checkBrowserSupport, parseBrowser, parseOS } from "@/shared/utils/browser";

const props = withDefaults(
  defineProps<{
    autoHideDuration?: number;
    customMessage?: string;
  }>(),
  {
    autoHideDuration: 8000,
    customMessage: "",
  },
);

/** 同一次会话只提示一次，避免刷新页面反复弹 */
const STORAGE_KEY = "browser-support-tip-shown";

const showBanner = ref(false);
const supportMessage = ref("");
let timer: ReturnType<typeof setTimeout> | null = null;

function closeBanner() {
  showBanner.value = false;
  if (!timer) {
    return;
  }

  clearTimeout(timer);
  timer = null;
}

onMounted(() => {
  if (typeof window === "undefined") return;

  const browser = parseBrowser();
  const os = parseOS();
  const support = checkBrowserSupport();

  console.info("[BrowserCheck]", { browser, os, support });

  if (support.ok) return;
  if (sessionStorage.getItem(STORAGE_KEY) === "1") return;

  supportMessage.value = support.message;
  sessionStorage.setItem(STORAGE_KEY, "1");
  showBanner.value = true;

  if (props.autoHideDuration > 0) {
    timer = setTimeout(() => {
      closeBanner();
    }, props.autoHideDuration);
  }
});

onUnmounted(() => {
  if (timer) {
    clearTimeout(timer);
  }
});

const displayMessage = computed(() => {
  if (props.customMessage) return props.customMessage;
  return supportMessage.value;
});
</script>

<style scoped>
.browser-check-prompt {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 360px;
  width: auto;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  font-family:
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    sans-serif;
  font-size: 14px;
  color: #fff;
  pointer-events: auto;
  border-left: 4px solid #ff9800;
}

.prompt-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
}

.prompt-icon {
  font-size: 20px;
  line-height: 1.2;
  flex-shrink: 0;
}

.prompt-text {
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
}

.prompt-text strong {
  font-weight: 600;
  display: inline-block;
  margin-bottom: 2px;
}

.prompt-close {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  margin: 0;
  line-height: 1;
  transition: color 0.2s;
  flex-shrink: 0;
}

.prompt-close:hover {
  color: #fff;
}

@media (max-width: 480px) {
  .browser-check-prompt {
    bottom: 12px;
    right: 12px;
    left: 12px;
    max-width: none;
  }

  .prompt-content {
    padding: 10px 12px;
  }
}
</style>
